import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextContact } from './Contact';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { Error } from '../../../models/error.model';
import { Message } from '../../../models/message.model';
import { Company } from '../../../models/company.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Contact } from '../../../models/contact.model';
import { collapsibleAction } from '../../../utils/collapsible.util';
import { modalShow } from '../../../utils/modal.util';
import { uploadTooltip } from '../../../utils/tooltip.util';
import { formatBIC, formatContact, formatCurrency, formatIBAN, formatNumberAddress, normalizeContactData } from '../../../scripts/format.value.script';
import { countryName } from '../../../libraries/country.library';
import { formatCantonDisplay, resolveCanton } from '../../../data/cantons';
import AppInputCanton from '../../../components/element/InputCanton';
import { languageValue } from '../../../libraries/language.library';
import { qrTypeValue } from '../../../libraries/qr.type.library';
import { contactTypeValue } from '../../../libraries/contact.type.library';
import { getError, getMessage } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import { ContactAddress } from '../../../models/contact.address.model';

import { isChatAllowed } from '../../../scripts/chat.auth';
import { openChat } from '../../../scripts/chat.controller';

import ContactAvatar from '../../../components/element/ContactAvatar';
import AppModalContactUpdate from '../../../components/modal/ModalContactUpdate';
import AppModalContactUpdateCompanies from '../../../components/modal/ModalContactUpdateCompanies';
import AppPreloader from '../../../components/Preloader';

export interface ContactViewPageProps {};

let errorResponse: Error, messageResponse: Message, contactResponse: Contact;

const ContactViewPage: React.FunctionComponent<ContactViewPageProps> = props => {
  const {setRoute, companyForUser, meCompanies, loadAllContacts, loadContactsForCompany} = UseOutletContextContact()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [contact, setContact] = useState<Contact | null>(null)
  const [contactCompanies, setContactCompanies] = useState<Company[] | null>(null)
  const [addresses, setAddresses] = useState<ContactAddress[]>([])
  const [editingAddress, setEditingAddress] = useState<ContactAddress | null>(null)
  const [addressForm, setAddressForm] = useState({ label: '', type: 'delivery', street: '', number: '', additional: '', administrative_area: '', city: '', zip_code: '', country: '', is_default_delivery: false })
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)

  const loadContact = async (id_company: string, id_contact: string) => {
    if (!companyForUser || !meCompanies) return null

    await ApiDigitalAdminContact.getContact(id_company, id_contact).then( (response) => {
      if (response.status === 200) {
        contactResponse = normalizeContactData(response.data)

        setContact(contactResponse)
        setContactCompanies(meCompanies.filter((item) => (contactResponse.data.companies.includes(item.id))))
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAddresses = async (id_company: string, id_contact: string) => {
    await ApiDigitalAdminContact.getContactAddresses(id_company, id_contact).then((response) => {
      if (response.status === 200) {
        setAddresses(response.data ?? [])
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const resetAddressForm = () => {
    setAddressForm({ label: '', type: 'delivery', street: '', number: '', additional: '', administrative_area: '', city: '', zip_code: '', country: '', is_default_delivery: false })
    setEditingAddress(null)
    setShowAddressForm(false)
  }

  const submitAddressSave = async () => {
    if (!companyForUser || !contact) return
    const cantonForBackend = addressForm.administrative_area;
    const address = { street: addressForm.street, number: addressForm.number, additional: addressForm.additional, administrative_area: cantonForBackend, city: addressForm.city, zip_code: addressForm.zip_code, country: addressForm.country }

    setSavingAddress(true)
    try {
      if (editingAddress) {
        const response = await ApiDigitalAdminContact.putContactAddress(companyForUser.id, contact.id, editingAddress.id, addressForm.label, addressForm.type, address, addressForm.is_default_delivery)
        if (response.status === 200) {
          await loadAddresses(companyForUser.id, contact.id)
          resetAddressForm()
          GlobalSwal.fire({ title: lang.labels.successfullyUpdated, icon: 'success', showConfirmButton: false, timer: 1500 })
        } else {
          errorResponse = response.data
          GlobalSwal.fire({ title: getError(errorResponse?.code, lang.code, errorResponse?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
        }
      } else {
        const response = await ApiDigitalAdminContact.postContactAddress(companyForUser.id, contact.id, addressForm.label, addressForm.type, address, addressForm.is_default_delivery)
        if (response.status === 200 || response.status === 201) {
          await loadAddresses(companyForUser.id, contact.id)
          resetAddressForm()
          GlobalSwal.fire({ title: lang.labels.successfullyCreated, icon: 'success', showConfirmButton: false, timer: 1500 })
        } else {
          errorResponse = response.data
          GlobalSwal.fire({ title: getError(errorResponse?.code, lang.code, errorResponse?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setSavingAddress(false)
    }
  }

  const submitAddressDelete = async (id_address: string) => {
    if (!companyForUser || !contact) return
    const result = await GlobalSwal.fire({
      title: lang.labels.youWantToDeleteThisAddress ?? 'Delete this address?',
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    })
    if (!result.isConfirmed) return

    try {
      const response = await ApiDigitalAdminContact.deleteContactAddress(companyForUser.id, contact.id, id_address)
      if (response.status === 200) {
        await loadAddresses(companyForUser.id, contact.id)
        GlobalSwal.fire({ title: lang.labels.successfullyDeleted, icon: 'success', showConfirmButton: false, timer: 1500 })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({ title: getError(errorResponse?.code, lang.code, errorResponse?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitSetDefaultDelivery = async (id_address: string) => {
    if (!companyForUser || !contact) return
    await ApiDigitalAdminContact.putContactAddressDefault(companyForUser.id, contact.id, id_address).then((response) => {
      if (response.status === 200) {
        loadAddresses(companyForUser.id, contact.id)
      }
    }).catch((error) => { errorWebHandler(lang, error) })
  }

  const editAddress = (addr: ContactAddress) => {
    setEditingAddress(addr)
    setAddressForm({
      label: addr.label,
      type: addr.type,
      street: addr.address?.street ?? '',
      number: addr.address?.number ?? '',
      additional: addr.address?.additional ?? '',
      administrative_area: (() => { const raw = addr.address?.administrative_area ?? ''; const r = resolveCanton(raw); return r ? r.code : raw; })(),
      city: addr.address?.city ?? '',
      zip_code: addr.address?.zip_code ?? '',
      country: addr.address?.country ?? '',
      is_default_delivery: addr.is_default_delivery
    })
    setShowAddressForm(true)
  }

  const submitContactDelete = async () => {
    if (!companyForUser || !contact) return null

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteThisContact,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAdminContact.deleteContacts(companyForUser.id, [contact.id]).then( (response) => {
          if (response.status === 200) {
            messageResponse = response.data
            loadAllContacts(companyForUser.id)
            loadContactsForCompany(companyForUser.id)

            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedRedirectingToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              navigate('/main/contact/list', {replace: true})
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const executeShowModalContactUpdate = () => {
    modalShow('modal-contact-update')
  }

  const executeShowModalContactCompanies = () => {
    modalShow('modal-contact-update-companies')
  }

  const executeCollapsible = () => {
    collapsibleAction('button-collapsible-details', 'container-collapsible-details')
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadContact(companyForUser.id, param.id)
      loadAddresses(companyForUser.id, param.id)
      uploadTooltip()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.contacts, branch: lang.labels.viewContact}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contactCompanies && contact
        ?
        <div className="d-flex flex-column flex-xl-row">
          <div className="flex-column flex-lg-row-auto w-100 w-xl-350px mb-5 mb-xl-0">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-center flex-column mb-10">
                  <div className="symbol symbol-100px symbol-circle mb-5">
                    <ContactAvatar
                      type={contact.type}
                      name={contact.data.name}
                      firstName={contact.data.first_name}
                      lastName={contact.data.last_name}
                      logo={contact.data.logo}
                      size={100}
                    />
                  </div>
                  <span className="fs-3 fw-bolder mb-3">
                    {contact.type === "person" ? `${contact.data.first_name ?? ''} ${contact.data.last_name ?? ''}`.trim() : `${contact.data.name}`}
                  </span>
                  <div>
                    <div className="badge badge-lg badge-light-primary d-inline text-uppercase">{contactTypeValue(lang, contact.type)}</div>
                  </div>
                </div>
                <div className="d-flex flex-stack fs-5">
                  <div id="button-collapsible-details" className="fw-bolder rotate collapsible" data-bs-toggle="collapse" role="button" onClick={executeCollapsible}>
                    {lang.labels.details}
                    <span className="ms-2 rotate-180">
                      <span className="svg-icon svg-icon-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black" />
                        </svg>
                      </span>
                    </span>
                  </div>
                </div>
                <div className="separator"></div>
                <div id="container-collapsible-details" className="collapse show">
                  <div className="fw-bold">
                    <div className="fs-6 mt-5 mb-2">{lang.labels.code}</div>
                    <div className="text-secondary">{contact.data.contact_code || lang.labels.none}</div>
                    <div className="fs-6 mt-5 mb-2">{lang.labels.userUuid}</div>
                    <div className="text-secondary">{(contact.data.uuid ?? '').length > 0 ? contact.data.uuid : lang.labels.contactIsNotAnAppUser}</div>
                    <div className="fs-6 mt-5 mb-2">{contact.data.companies.length === 1 ? lang.labels.company : lang.labels.companies}</div>
                    { contactCompanies.map (( (item, index) => { return (
                      <div key={index} className={`text-secondary ${contact.data.companies.length > 1 && "d-flex align-items-center mb-1"}`}>
                        {item.name}
                        { contactCompanies.length > 1 && item.id === companyForUser.id &&
                          <span className="badge badge-sm badge-light fw-bolder fs-8 ms-3">
                            <small className="text-uppercase">{lang.labels.current}</small>
                          </span>
                        }
                      </div>
                    )}))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-lg-row-fluid ms-lg-5">
            <ul className="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-5 fw-bolder ms-2 mb-5">
              <li className="nav-item mb-1">
                <a href="#container-contact-identity" className="nav-link text-active-primary active" data-bs-toggle="tab">{lang.labels.identity}</a>
              </li>
              <li className="nav-item mb-1">
                <a href="#container-contact-information" className="nav-link text-active-primary" data-bs-toggle="tab">{lang.labels.information}</a>
              </li>
              <li className="nav-item mb-1">
                <a href="#container-contact-legal" className="nav-link text-active-primary" data-bs-toggle="tab">{lang.labels.legal}</a>
              </li>
              <li className="nav-item mb-1">
                <a href="#container-contact-addresses" className="nav-link text-active-primary" data-bs-toggle="tab">{lang.labels.addresses ?? 'Addresses'}</a>
              </li>
              <li className="nav-item mb-1">
                <a href="#container-contact-settings" className="nav-link text-active-primary" data-bs-toggle="tab">{lang.labels.settings}</a>
              </li>
              <li className="nav-item ms-auto mt-1">
                <div className="d-flex flex-wrap">
                  {isChatAllowed() && contact.is_user && contact.data.uuid && (
                    <button
                      className="btn btn-sm btn-success ms-2"
                      type="button"
                      onClick={() => openChat({ contactId: contact.id, contactName: formatContact(contact), contactType: contact.type as 'person' | 'company', contactUuid: contact.data.uuid })}
                    >
                      {lang.labels.chatStartConversation}
                    </button>
                  )}
                  <Link to={"/main/contact/entry/" + contact.id} className="btn btn-sm btn-primary ms-2">
                    {lang.labels.accountStatement}
                  </Link>
                  <Dropdown>
                    <Dropdown.Toggle variant="btn btn-sm btn-primary ms-2">
                      <div className="d-flex align-items-center">
                        {lang.labels.actions}
                        <span className="svg-icon svg-icon-2 me-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                          </svg>
                        </span>
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-250px p-3" align="end">
                      <Dropdown.Item bsPrefix="menu-item" onClick={executeShowModalContactUpdate}>
                        <p className="menu-link fw-bold px-5 m-0">{lang.labels.editContact}</p>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item" onClick={submitContactDelete}>
                        <p className="menu-link fw-bold px-5 m-0">{lang.labels.deleteContact}</p>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item" onClick={executeShowModalContactCompanies}>
                        <p className="menu-link fw-bold px-5 m-0">{lang.labels.addOrRemoveCompanies}</p>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </li>
            </ul>
            <div className="tab-content">
              <div id="container-contact-identity" className="tab-pane fade show active" role="tabpanel">
                <div className="card card-flush">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.identityOfContact}</h4>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <table className="table align-middle gy-3 m-0">
                      <tbody className="fw-bold text-secondary">
                        { contact.type === "person"
                          ?
                          <>
                            <tr>
                              <td className="w-200px text-black">{lang.labels.firstName}</td>
                              <td className="min-w-200px fs-6">{contact.data.first_name}</td>
                            </tr>
                            <tr>
                              <td className="w-200px text-black">{lang.labels.lastName}</td>
                              <td className="min-w-200px fs-6">{contact.data.last_name}</td>
                            </tr>
                          </>
                          :
                          <tr>
                            <td className="w-200px text-black">{lang.labels.name}</td>
                            <td className="min-w-200px fs-6">{contact.data.name}</td>
                          </tr>
                        }
                        <tr>
                          <td className="w-200px text-black">{lang.labels.phone}</td>
                          <td className="min-w-200px fs-6">{(contact.data.phone ?? '').length > 0 ? `${contact.data.phone}` : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.email}</td>
                          <td className="min-w-200px fs-6">{(contact.data.email ?? '').length > 0 ? `${contact.data.email}` : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.webSite}</td>
                          <td className="min-w-200px fs-6">{(contact.data.website ?? '').length > 0 ? `${contact.data.website}` : lang.labels.none}</td>
                        </tr>
                        { contact.type === "company" &&
                          <>
                            <tr>
                              <td className="w-200px text-black">{lang.labels.registerNumber}</td>
                              <td className="min-w-200px fs-6">{(contact.data.register_number ?? '').length > 0 ? `${contact.data.register_number}` : lang.labels.none}</td>
                            </tr>
                            <tr>
                              <td className="w-200px text-black">{lang.labels.vatNumber}</td>
                              <td className="min-w-200px fs-6">{(contact.data.vat_number ?? '').length > 0 ? `${contact.data.vat_number}` : lang.labels.none}</td>
                            </tr>
                          </>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div id="container-contact-information" className="tab-pane fade" role="tabpanel">
                <div className="card card-flush">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.informationOfContact}</h4>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <table className="table align-middle gy-3 m-0">
                      <tbody className="fw-bold text-secondary">
                        <tr>
                          <td className="w-200px text-black">{lang.labels.street}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.street ?? '').length > 0 ? contact.data.address.street : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.number}</td>
                          <td className="min-w-200px fs-6">{formatNumberAddress(lang, contact.data.address?.number)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.additional}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.additional ?? '').length > 0 ? contact.data.address.additional : lang.labels.none}</td>
                        </tr>
                        <tr>
                        <td className="w-200px text-black">{lang.labels.administrativeArea}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.administrative_area ?? '').length > 0 ? formatCantonDisplay(contact.data.address.administrative_area, lang.code) : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.zipCode}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.zip_code ?? '').length > 0 ? contact.data.address.zip_code : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.city}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.city ?? '').length > 0 ? contact.data.address.city : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">{lang.labels.country}</td>
                          <td className="min-w-200px fs-6">{(contact.data.address?.country ?? '').length > 0 ? countryName(lang.code, contact.data.address.country) : lang.labels.none}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div id="container-contact-legal" className="tab-pane fade" role="tabpanel">
                <div className="card card-flush">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.legalInformationOfContact}</h4>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <table className="table align-middle gy-3 m-0">
                      <tbody className="fw-bold text-secondary">
                        { contact.type === "company" &&
                          <tr>
                            <td className="w-200px text-black">{lang.labels.currency}</td>
                            <td className="min-w-200px fs-6">{formatCurrency(contact.data.currency)}</td>
                          </tr>
                        }
                        <tr>
                          <td className="w-200px text-black">{lang.labels.financialEntity}</td>
                          <td className="min-w-200px fs-6">{(contact.data.bank_account?.name ?? '').length > 0 ? `${contact.data.bank_account.name}` : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">
                            <div className="d-flex align-items-center">
                              {lang.labels.iban}
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                            </div>
                          </td>
                          <td className="min-w-200px fs-6">{formatIBAN(lang, contact.data.bank_account?.iban)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">
                            <div className="d-flex align-items-center">
                              {lang.labels.bban}
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                            </div>
                          </td>
                          <td className="min-w-200px fs-6">{(contact.data.bank_account?.bban ?? '').length > 0 ? `${contact.data.bank_account.bban}` : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">
                            <div className="d-flex align-items-center">
                              {lang.labels.bicSwift}
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                            </div>
                          </td>
                          <td className="min-w-200px fs-6">{formatBIC(lang, contact.data.bank_account?.bic)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div id="container-contact-addresses" className="tab-pane fade" role="tabpanel">
                <div className="card card-flush">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.addresses ?? 'Addresses'}</h4>
                    </div>
                    <div className="card-toolbar">
                      <button className="btn btn-sm btn-primary" type="button" onClick={() => { resetAddressForm(); setShowAddressForm(true) }}>
                        {lang.labels.addAddress ?? 'Add address'}
                      </button>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    { showAddressForm &&
                      <div className="border rounded p-5 mb-5">
                        <h5 className="mb-4">{editingAddress ? (lang.labels.editAddress ?? 'Edit address') : (lang.labels.addAddress ?? 'Add address')}</h5>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">{lang.labels.addressLabel ?? 'Label'}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.label} onChange={(e) => setAddressForm({...addressForm, label: e.target.value})} placeholder={lang.labels.addressLabel ?? 'e.g. Main office'} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.addressType ?? 'Type'}</label>
                            <select className="form-select form-select-sm" value={addressForm.type} onChange={(e) => setAddressForm({...addressForm, type: e.target.value})}>
                              <option value="delivery">{lang.labels.delivery ?? 'Delivery'}</option>
                              <option value="billing">{lang.labels.billing ?? 'Billing'}</option>
                              <option value="other">{lang.labels.other ?? 'Other'}</option>
                            </select>
                          </div>
                          <div className="col-md-3 d-flex align-items-end">
                            <div className="form-check">
                              <input type="checkbox" className="form-check-input" checked={addressForm.is_default_delivery} onChange={(e) => setAddressForm({...addressForm, is_default_delivery: e.target.checked})} />
                              <label className="form-check-label">{lang.labels.defaultDeliveryAddress ?? 'Default delivery'}</label>
                            </div>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">{lang.labels.street}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.street} onChange={(e) => setAddressForm({...addressForm, street: e.target.value})} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.number}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.number} onChange={(e) => setAddressForm({...addressForm, number: e.target.value})} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.additional}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.additional} onChange={(e) => setAddressForm({...addressForm, additional: e.target.value})} />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.zipCode}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.zip_code} onChange={(e) => setAddressForm({...addressForm, zip_code: e.target.value})} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.city}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.city} onChange={(e) => setAddressForm({...addressForm, city: e.target.value})} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.administrativeArea}</label>
                            {addressForm.country === 'CH'
                              ? <AppInputCanton
                                  canton={{value: addressForm.administrative_area, valid: true}}
                                  setCanton={(val) => {
                                    const v = typeof val === 'function' ? val({value: addressForm.administrative_area, valid: true}) : val;
                                    setAddressForm({...addressForm, administrative_area: v.value});
                                  }}
                                  options={{disabled: false}}
                                />
                              : <input type="text" className="form-control form-control-sm" value={addressForm.administrative_area} onChange={(e) => setAddressForm({...addressForm, administrative_area: e.target.value})} />
                            }
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold">{lang.labels.country}</label>
                            <input type="text" className="form-control form-control-sm" value={addressForm.country} onChange={(e) => setAddressForm({...addressForm, country: e.target.value})} />
                          </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                          <button className="btn btn-sm btn-secondary" type="button" onClick={resetAddressForm}>{lang.labels.cancel}</button>
                          <button className="btn btn-sm btn-primary" type="button" onClick={submitAddressSave} disabled={!addressForm.label || savingAddress}>
                            {savingAddress && <span className="spinner-border spinner-border-sm me-2"></span>}
                            {lang.labels.save}
                          </button>
                        </div>
                      </div>
                    }
                    { addresses.length > 0
                      ?
                      <div className="table-responsive">
                        <table className="table align-middle table-row-dashed gy-3">
                          <thead>
                            <tr className="fw-bold text-muted text-uppercase fs-7">
                              <th>{lang.labels.addressLabel ?? 'Label'}</th>
                              <th>{lang.labels.addressType ?? 'Type'}</th>
                              <th>{lang.labels.address}</th>
                              <th>{lang.labels.defaultDeliveryAddress ?? 'Default'}</th>
                              <th className="text-end">{lang.labels.actions}</th>
                            </tr>
                          </thead>
                          <tbody className="fw-bold text-secondary">
                            { addresses.map((addr: ContactAddress) => (
                              <tr key={addr.id}>
                                <td>{addr.label}</td>
                                <td><span className="badge badge-light text-uppercase">{addr.type}</span></td>
                                <td>
                                  {addr.address?.street ?? ''} {addr.address?.number ?? ''}{addr.address?.additional ? `, ${addr.address.additional}` : ''}<br/>
                                  {addr.address?.zip_code ?? ''} {addr.address?.city ?? ''}{addr.address?.country ? `, ${countryName(lang.code, addr.address.country)}` : ''}
                                </td>
                                <td>
                                  { addr.is_default_delivery
                                    ? <span className="badge badge-light-success">{lang.labels.yes}</span>
                                    : <button className="btn btn-sm btn-light" type="button" onClick={() => submitSetDefaultDelivery(addr.id)}>{lang.labels.setAsDefault ?? 'Set as default'}</button>
                                  }
                                </td>
                                <td className="text-end">
                                  <button className="btn btn-sm btn-icon btn-light-primary me-1" type="button" onClick={() => editAddress(addr)}>
                                    <span className="svg-icon svg-icon-3">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path opacity="0.3" d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 1.99988 17.0825 1.99988C17.6224 1.99988 18.1402 2.21423 18.522 2.59595L21.4 5.474C21.7817 5.85581 21.9962 6.37355 21.9962 6.91345C21.9962 7.45335 21.7817 7.97122 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75641 21.9115 2.989 21.9658C3.22158 22.0201 3.4647 22.0084 3.69099 21.932H3.68699Z" fill="black"/>
                                        <path d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.3051L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </button>
                                  <button className="btn btn-sm btn-icon btn-light-danger" type="button" onClick={() => submitAddressDelete(addr.id)}>
                                    <span className="svg-icon svg-icon-3">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black"/>
                                        <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black"/>
                                        <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      :
                      <div className="text-center text-muted py-10">
                        {lang.labels.noAddressesYet ?? 'No addresses added yet'}
                      </div>
                    }
                  </div>
                </div>
              </div>
              <div id="container-contact-settings" className="tab-pane fade" role="tabpanel">
                <div className="card card-flush">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.fileDownloadSettings}</h4>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <table className="table align-middle gy-3 m-0">
                      <tbody className="fw-bold text-secondary">
                        <tr>
                          <td className="w-200px text-black">
                            <div className="d-flex align-items-center">
                              {lang.labels.language}
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.languageToDownload}><FaExclamationCircle /></i>
                            </div>
                          </td>
                          <td className="text-capitalize">{languageValue(lang, contact.data.language)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-black">
                            <div className="d-flex align-items-center">
                              {lang.labels.qrType}
                              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.shapesOrVersionsOfQR}><FaExclamationCircle /></i>
                            </div>
                          </td>
                          <td className="min-w-200px fs-6">{qrTypeValue(lang, contact.data.qr_type)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalContactUpdate companyForUser={companyForUser} meCompanies={meCompanies} contact={contact} contactCompanies={contactCompanies} setContact={setContact} setContactCompanies={setContactCompanies} loadAllContacts={loadAllContacts} loadContactsForCompany={loadContactsForCompany}></AppModalContactUpdate>
      <AppModalContactUpdateCompanies companyForUser={companyForUser} meCompanies={meCompanies} contact={contact} setContact={setContact} setContactCompanies={setContactCompanies}></AppModalContactUpdateCompanies>
    </div>
  )
};

export default ContactViewPage;
